import * as Sentry from '@sentry/react';
import appConfig from '../config';

class SentryService {
  private static instance: SentryService | null = null;
  private isInitialized = false;
  private dsn: string;
  private environment: string;
  private isEnabled: boolean;

  private serviceConfig = {
    silentMode: false,
  };

  constructor() {
    this.dsn = appConfig.sentry.dsn;
    this.environment = appConfig.sentry.environment;
    this.isEnabled = !!this.dsn;
  }

  static getInstance(): SentryService {
    if (!SentryService.instance) {
      SentryService.instance = new SentryService();
    }
    return SentryService.instance;
  }

  get shouldInitialize(): boolean {
    return this.isEnabled && !!this.dsn;
  }

  get isActive(): boolean {
    return this.isInitialized && this.shouldInitialize;
  }

  initialize(): void {
    if (this.isInitialized || !this.shouldInitialize) {
      this.log('log', 'Sentry initialization skipped:', {
        isInitialized: this.isInitialized,
        shouldInitialize: this.shouldInitialize,
        dsn: this.dsn ? '[SET]' : '[NOT SET]',
        isEnabled: this.isEnabled,
      });
      return;
    }

    try {
      Sentry.init({
        dsn: this.dsn,
        environment: this.environment,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });

      this.isInitialized = true;
      this.log('log', '✅ Sentry initialized successfully for environment:', this.environment);
    } catch (error) {
      this.log('error', '❌ Failed to initialize Sentry:', error);
    }
  }

  identifyUser(userInfo: any): void {
    if (!this.isInitialized || !this.shouldInitialize) return;

    try {
      Sentry.setUser({
        id: userInfo?.userInfo?.id || userInfo?.id || undefined,
        email: userInfo?.userInfo?.email || userInfo?.email || undefined,
        username: userInfo?.userInfo?.name || userInfo?.name || undefined,
      });
      this.log('log', '✅ User identified in Sentry');
    } catch (error) {
      this.log('error', '❌ Failed to identify user in Sentry:', error);
    }
  }

  captureError(error: Error, context?: Record<string, unknown>): void {
    if (!this.isInitialized || !this.shouldInitialize) return;

    try {
      Sentry.captureException(error, { extra: context });
      this.log('log', '❌ Error captured in Sentry:', error.message);
    } catch (sentryError) {
      this.log('error', '❌ Failed to capture error in Sentry:', sentryError);
    }
  }

  getStatus(): Record<string, unknown> {
    try {
      const client = Sentry.getClient();
      return {
        isInitialized: this.isInitialized,
        isEnabled: this.isEnabled,
        dsn: this.dsn ? '[SET]' : '[NOT SET]',
        environment: this.environment,
        shouldInitialize: this.shouldInitialize,
        clientActive: !!client,
        currentDomain: window.location.hostname,
      };
    } catch (error) {
      return { error: 'Failed to get Sentry status', details: error };
    }
  }

  private log(level: 'log' | 'warn' | 'error', ...args: unknown[]): void {
    if (!this.serviceConfig.silentMode) {
      console[level](...args);
    }
  }
}

export default SentryService;
